import { Stat } from '../../../components/office';
import { BRAND } from '@verlo/shared/lib/brand';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, ChevronLeft, ChevronRight, AlertCircle, TrendingUp,
  Calendar, Coins, Receipt, Ban, Filter, X as XIcon, Keyboard,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import VerloInsightPanel from '../../../components/reports/VerloInsightPanel';
import api from '@verlo/shared/services/api';
import { Input } from '@verlo/shared/components/ui/input';

const EVENT_META = {
  high_value:        { label: 'High value',      color: '#059669', bg: 'bg-signal-ok-tint',  text: 'text-signal-ok', dot: 'bg-signal-ok-line', icon: TrendingUp,    anomaly: false },
  void:              { label: 'Void',            color: '#DC2626', bg: 'bg-signal-stop-tint',     text: 'text-signal-stop',    dot: 'bg-signal-stop-line',    icon: Ban,           anomaly: true  },
  refund:            { label: 'Refund',          color: '#EA580C', bg: 'bg-signal-warn-tint',   text: 'text-signal-warn',  dot: 'bg-signal-warn-line',  icon: Receipt,       anomaly: true  },
  discount:          { label: 'Discount',        color: '#7C3AED', bg: 'bg-signal-info-tint',   text: 'text-signal-info',  dot: 'bg-signal-info-line',  icon: Coins,         anomaly: false },
  manager_override:  { label: 'Mgr override',    color: '#0891B2', bg: 'bg-signal-info-tint',     text: 'text-signal-info',    dot: 'bg-signal-info-line',    icon: AlertCircle,   anomaly: true  },
};

const fmtMoney = (v) => v === null || v === undefined ? '—' : `£${Math.round(v).toLocaleString('en-GB')}`;
const pad = (n) => String(n).padStart(2, '0');
const fmtTime = (iso) => {
  try { const d = new Date(iso); return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`; }
  catch { return ''; }
};
const stepDate = (iso, delta) => {
  const d = new Date(iso); d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

const ServiceModeTimelinePage = () => {
  const [date, setDate] = useState('');           // empty = let backend pick latest real day
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredBucket, setHoveredBucket] = useState(null);
  const [anomaliesOnly, setAnomaliesOnly] = useState(false);
  // Brush selection — indices of the 96-bucket revenue lane
  const [brush, setBrush] = useState(null); // { startIdx, endIdx } or null
  const [drag, setDrag] = useState(null);   // active drag: { startIdx, currentIdx }
  const svgRef = useRef(null);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await api.get('/reports/service/timeline', { params: d ? { date: d } : {} });
      setData(r.data);
      if (!date) setDate(r.data.date);
      setBrush(null); // reset brush whenever the day changes
    } catch (e) {
      toast.error('Could not load timeline');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(date || null); /* eslint-disable-next-line */ }, []);

  // Keyboard arrow navigation (← / →) — skip when the date input is focused
  useEffect(() => {
    const onKey = (e) => {
      if (!date) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); onChangeDate(stepDate(date, -1)); }
      if (e.key === 'ArrowRight') { e.preventDefault(); onChangeDate(stepDate(date, 1)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [date]);

  const onChangeDate = (next) => { setDate(next); load(next); };

  const chartGeom = useMemo(() => {
    if (!data || !data.revenue_lane.length) return null;
    const W = 1000, H = 180, padX = 40, padY = 14;
    const maxRev = Math.max(...data.revenue_lane.map((b) => b.interval_revenue), 1);
    const len = data.revenue_lane.length;
    const stepX = (W - padX * 2) / (len - 1);
    const pts = data.revenue_lane.map((b, i) => {
      const x = padX + i * stepX;
      const y = H - padY - ((b.interval_revenue / maxRev) * (H - padY * 2));
      return { x, y, b };
    });
    const area = `M ${padX},${H - padY} ` +
      pts.map((p) => `L ${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') +
      ` L ${(padX + stepX * (len - 1)).toFixed(1)},${H - padY} Z`;
    const line = `M ` + pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ');
    return { W, H, padX, padY, maxRev, stepX, pts, area, line };
  }, [data]);

  const eventsOnTimeline = useMemo(() => {
    if (!data || !chartGeom) return [];
    return data.events.map((e) => {
      try {
        const d = new Date(e.time);
        const minutes = d.getUTCHours() * 60 + d.getUTCMinutes();
        const idx = Math.min(95, Math.floor(minutes / 15));
        const x = chartGeom.padX + idx * chartGeom.stepX;
        return { ...e, x, bucketIdx: idx };
      } catch { return null; }
    }).filter(Boolean);
  }, [data, chartGeom]);

  // Active brush window (final selection OR live drag)
  const activeBrush = useMemo(() => {
    if (drag) {
      const a = Math.min(drag.startIdx, drag.currentIdx);
      const b = Math.max(drag.startIdx, drag.currentIdx);
      return { startIdx: a, endIdx: b };
    }
    return brush;
  }, [drag, brush]);

  // Brush slice aggregates
  const brushSummary = useMemo(() => {
    if (!activeBrush || !data) return null;
    const lane = data.revenue_lane.slice(activeBrush.startIdx, activeBrush.endIdx + 1);
    const revenue = lane.reduce((s, b) => s + b.interval_revenue, 0);
    const sliceEvents = eventsOnTimeline.filter((e) => e.bucketIdx >= activeBrush.startIdx && e.bucketIdx <= activeBrush.endIdx);
    const byType = {};
    for (const e of sliceEvents) byType[e.type] = (byType[e.type] || 0) + 1;
    const fromMin = activeBrush.startIdx * 15;
    const toMin = (activeBrush.endIdx + 1) * 15;
    return {
      revenue,
      events: sliceEvents.length,
      byType,
      fromLabel: `${pad(Math.floor(fromMin / 60))}:${pad(fromMin % 60)}`,
      toLabel:   `${pad(Math.floor(toMin / 60) % 24)}:${pad(toMin % 60)}`,
    };
  }, [activeBrush, data, eventsOnTimeline]);

  // Convert SVG mouse X → bucket index
  const pickBucketIdx = (clientX) => {
    if (!svgRef.current || !chartGeom) return 0;
    const rect = svgRef.current.getBoundingClientRect();
    const ratio = chartGeom.W / rect.width;
    const localX = (clientX - rect.left) * ratio;
    const idx = Math.round((localX - chartGeom.padX) / chartGeom.stepX);
    return Math.max(0, Math.min(95, idx));
  };

  const onBrushDown = (e) => {
    const idx = pickBucketIdx(e.clientX);
    setDrag({ startIdx: idx, currentIdx: idx });
  };
  const onBrushMove = (e) => {
    if (!drag) return;
    const idx = pickBucketIdx(e.clientX);
    if (idx !== drag.currentIdx) setDrag({ ...drag, currentIdx: idx });
  };
  const onBrushUp = () => {
    if (!drag) return;
    const a = Math.min(drag.startIdx, drag.currentIdx);
    const b = Math.max(drag.startIdx, drag.currentIdx);
    setDrag(null);
    if (b - a >= 1) setBrush({ startIdx: a, endIdx: b }); // ignore single-cell clicks
    else setBrush(null);
  };

  const visibleEvents = useMemo(() => {
    if (!data) return [];
    let evs = data.events;
    if (anomaliesOnly) evs = evs.filter((e) => EVENT_META[e.type]?.anomaly);
    return evs;
  }, [data, anomaliesOnly]);

  const anomalyCount = useMemo(() => (
    data ? data.events.filter((e) => EVENT_META[e.type]?.anomaly).length : 0
  ), [data]);

  return (
    <ReportsLayout>
      <div data-testid="service-mode-page">
        {/* Header */}
        <div className="flex min-h-[36px] items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[14px] text-ink-soft max-w-2xl">
              Every minute, every spike, every voided check rendered on a single horizontal scrubber. Treat each day like a recording you can rewatch with the team.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:inline-flex items-center gap-1 text-[12.5px] text-ink-faint mr-1" title="Use ← / → to step day">
              <Keyboard size={11} />← →
            </span>
            <button onClick={() => onChangeDate(stepDate(date, -1))} className="h-9 w-9 rounded-md bg-white border border-hairline hover:border-ink-faint flex items-center justify-center" data-testid="service-prev-day">
              <ChevronLeft size={14} />
            </button>
            <div className="flex items-center gap-2 bg-white border border-hairline rounded-md px-3 h-9">
              <Calendar size={13} className="text-ink-faint" />
              <Input
                type="date"
                value={date}
                onChange={(e) => onChangeDate(e.target.value)}
                className="h-7 w-[150px] border-0 p-0 text-[12px] font-mono-display"
                data-testid="service-date-input"
              />
            </div>
            <button onClick={() => onChangeDate(stepDate(date, 1))} className="h-9 w-9 rounded-md bg-white border border-hairline hover:border-ink-faint flex items-center justify-center" data-testid="service-next-day">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Stats strip */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3 mb-5" data-testid="service-summary-tiles">
            <StatTile label="Revenue" value={fmtMoney(data.summary.total_revenue)} accent="default" />
            <StatTile label="Orders" value={data.summary.orders} />
            <StatTile label="Covers" value={data.summary.covers} />
            <StatTile label="AOV" value={fmtMoney(data.summary.aov)} />
            <StatTile label="Peak hour" value={data.summary.biggest_spike_hour !== null ? `${pad(data.summary.biggest_spike_hour)}:00` : '—'} hint={fmtMoney(data.summary.biggest_spike_revenue)} />
            <StatTile label="Anomalies" value={data.summary.void_count + data.summary.refund_count + data.summary.override_count} hint={`${data.summary.void_count}V · ${data.summary.refund_count}R · ${data.summary.override_count}O`} tone={(data.summary.void_count + data.summary.refund_count + data.summary.override_count) > 0 ? 'rose' : 'default'} />
          </div>
        )}

        {/* Brush summary banner */}
        {brushSummary && (
          <div className="bg-verlo/[0.04] border border-verlo/20 rounded-lg px-4 py-3 mb-5 flex items-center gap-4 flex-wrap" data-testid="service-brush-summary">
            <div className="font-mono-display text-[13px] text-ink">
              {brushSummary.fromLabel} → {brushSummary.toLabel}
            </div>
            <div className="text-[12px] text-ink-soft">
              <span className="font-semibold text-ink font-mono-display">{fmtMoney(brushSummary.revenue)}</span> in selection
            </div>
            <div className="text-[12px] text-ink-soft">
              <span className="font-semibold text-ink font-mono-display">{brushSummary.events}</span> events
            </div>
            {Object.entries(brushSummary.byType).map(([k, v]) => {
              const meta = EVENT_META[k];
              if (!meta) return null;
              return (
                <span key={k} className={`inline-flex items-center gap-1 text-[12.5px] ${meta.text} ${meta.bg} px-2 py-0.5 rounded`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                  {v} {meta.label.toLowerCase()}
                </span>
              );
            })}
            <button onClick={() => setBrush(null)} className="ml-auto text-ink-soft hover:text-ink inline-flex items-center gap-1 text-[12.5px]" data-testid="service-brush-clear">
              <XIcon size={11} /> Clear window
            </button>
          </div>
        )}

        {/* Timeline canvas */}
        <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden mb-5" data-testid="service-timeline-canvas">
          <div className="px-5 py-3 border-b border-hairline flex items-center justify-between flex-wrap gap-2 sticky top-0 bg-white z-10">
            <div>
              <h3 className="text-sm font-semibold text-ink">{date || 'Loading…'}</h3>
              <p className="text-[12.5px] text-ink-soft mt-0.5">
                {data?.data_source === 'shifted' ? 'No orders on this date, so this shows the nearest day with data.' : 'Revenue in 15-min buckets · drag across the chart to select a window'}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {Object.entries(EVENT_META).map(([k, m]) => (
                <span key={k} className="inline-flex items-center gap-1 text-[12.5px] text-ink-soft">
                  <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                  {m.label}
                </span>
              ))}
            </div>
          </div>
          <div className="p-4 overflow-x-auto">
            {loading && <div className="h-[260px] flex items-center justify-center text-ink-faint text-sm">Loading service…</div>}
            {!loading && chartGeom && (
              <svg
                ref={svgRef}
                viewBox={`0 0 ${chartGeom.W} 260`}
                className="w-full select-none"
                preserveAspectRatio="none"
                data-testid="service-svg"
                onMouseDown={onBrushDown}
                onMouseMove={onBrushMove}
                onMouseUp={onBrushUp}
                onMouseLeave={() => drag && onBrushUp()}
                style={{ cursor: drag ? 'ew-resize' : 'crosshair' }}
              >
                <defs>
                  <linearGradient id="svc-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND.verlo} stopOpacity="0.18" />
                    <stop offset="100%" stopColor={BRAND.verlo} stopOpacity="0.02" />
                  </linearGradient>
                </defs>
                {/* Hour gridlines */}
                {Array.from({ length: 25 }).map((_, h) => {
                  const x = chartGeom.padX + (h * 4) * chartGeom.stepX;
                  return (
                    <g key={h}>
                      <line x1={x} y1={14} x2={x} y2={chartGeom.H - chartGeom.padY} stroke="#F1F5F9" strokeWidth={h % 6 === 0 ? 1 : 0.5} strokeDasharray={h % 6 === 0 ? 0 : '2 3'} />
                      {h % 3 === 0 && <text x={x} y={chartGeom.H + 8} fill="#94A3B8" fontSize="9" textAnchor="middle">{pad(h)}:00</text>}
                    </g>
                  );
                })}
                {/* Active brush window */}
                {activeBrush && (() => {
                  const x1 = chartGeom.padX + activeBrush.startIdx * chartGeom.stepX;
                  const x2 = chartGeom.padX + activeBrush.endIdx * chartGeom.stepX;
                  return (
                    <g pointerEvents="none">
                      <rect x={x1} y={14} width={Math.max(2, x2 - x1)} height={chartGeom.H - chartGeom.padY - 14} fill={BRAND.verlo} fillOpacity="0.08" />
                      <line x1={x1} y1={14} x2={x1} y2={chartGeom.H - chartGeom.padY} stroke={BRAND.verlo} strokeWidth="1" strokeDasharray="3 2" />
                      <line x1={x2} y1={14} x2={x2} y2={chartGeom.H - chartGeom.padY} stroke={BRAND.verlo} strokeWidth="1" strokeDasharray="3 2" />
                    </g>
                  );
                })()}
                {/* Revenue area + line */}
                <path d={chartGeom.area} fill="url(#svc-grad)" pointerEvents="none" />
                <path d={chartGeom.line} fill="none" stroke={BRAND.verlo} strokeWidth="1.5" strokeLinejoin="round" pointerEvents="none" />
                {/* Hover dots */}
                {chartGeom.pts.map((p, i) => p.b.interval_revenue > 0 && (
                  <circle
                    key={i}
                    cx={p.x} cy={p.y} r="2.5"
                    fill="#fff" stroke={BRAND.verlo} strokeWidth="1"
                    onMouseEnter={() => setHoveredBucket(p.b)}
                    onMouseLeave={() => setHoveredBucket(null)}
                  />
                ))}

                {/* Event markers row */}
                {eventsOnTimeline.map((e, i) => {
                  const meta = EVENT_META[e.type] || EVENT_META.high_value;
                  return (
                    <g key={i} transform={`translate(${e.x}, 210)`} pointerEvents="none">
                      <circle r="4" fill={meta.color} stroke="#fff" strokeWidth="1.5">
                        <title>{`${meta.label} · ${fmtTime(e.time)} · ${e.staff_name} · ${e.detail}`}</title>
                      </circle>
                    </g>
                  );
                })}

                {/* X-axis baseline */}
                <line x1={chartGeom.padX} y1={chartGeom.H - chartGeom.padY} x2={chartGeom.padX + chartGeom.stepX * 95} y2={chartGeom.H - chartGeom.padY} stroke="#E2E8F0" strokeWidth="1" pointerEvents="none" />
                <text x={chartGeom.padX} y={232} fill="#64748B" fontSize="9" fontWeight="600" pointerEvents="none">Events</text>
              </svg>
            )}
            {/* Hover tooltip */}
            {hoveredBucket && (
              <div className="text-[12.5px] text-ink-soft mt-2">
                <span className="font-mono-display">{pad(Math.floor(hoveredBucket.minute / 60))}:{pad(hoveredBucket.minute % 60)}</span>
                <span className="mx-2 text-ink-faint">·</span>
                <span className="text-ink font-semibold font-mono">{fmtMoney(hoveredBucket.interval_revenue)} in this 15-min window</span>
                <span className="mx-2 text-ink-faint">·</span>
                <span className="text-ink-soft">Running total {fmtMoney(hoveredBucket.cumulative_revenue)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Events list */}
        {data && data.events.length > 0 && (
          <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden mb-5" data-testid="service-events-table">
            <div className="px-5 py-3 border-b border-hairline flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold text-ink">Service log</h3>
                <p className="text-[12.5px] text-ink-soft mt-0.5">
                  {anomaliesOnly
                    ? `${visibleEvents.length} of ${data.events.length} flagged · anomalies only`
                    : `${data.events.length} events · chronological`}
                </p>
              </div>
              <button
                onClick={() => setAnomaliesOnly((v) => !v)}
                disabled={anomalyCount === 0}
                className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${
                  anomaliesOnly
                    ? 'bg-signal-stop text-white border-signal-stop hover:bg-signal-stop'
                    : 'bg-white text-ink-soft border-hairline hover:border-ink-faint disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
                data-testid="service-anomalies-filter"
              >
                <Filter size={11} />
                {anomaliesOnly ? 'Anomalies only' : `Anomalies only · ${anomalyCount}`}
              </button>
            </div>
            <div className="divide-y divide-hairline max-h-[420px] overflow-y-auto">
              {visibleEvents.length === 0 && (
                <div className="px-5 py-10 text-center text-[12px] text-ink-faint">
                  Nothing to show. Try clearing the filter.
                </div>
              )}
              {visibleEvents.map((e, i) => {
                const meta = EVENT_META[e.type] || EVENT_META.high_value;
                const Icon = meta.icon;
                return (
                  <div key={i} className="flex items-center gap-3 px-5 py-2.5 hover:bg-hover-tint/60">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${meta.bg}`}>
                      <Icon size={13} className={meta.text} />
                    </span>
                    <span className="font-mono-display text-[12.5px] text-ink-soft w-12">{fmtTime(e.time)}</span>
                    <span className={`inline-flex items-center text-[12.5px] font-semibold px-1.5 py-0.5 rounded ${meta.bg} ${meta.text}`}>{meta.label}</span>
                    <span className="text-[12px] text-ink-soft flex-1">{e.detail}</span>
                    <span className="text-[12.5px] text-ink-soft">{e.staff_name}</span>
                    <span className="font-mono-display text-[12px] font-semibold text-ink w-20 text-right">{fmtMoney(e.amount)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <VerloInsightPanel
          topic="trading_window"
          range="last_30"
          label="The most profitable trading window"
          hint="Tap Generate for a 2-sentence read on when and why this period of the day fires, and how to amplify it."
        />
      </div>
    </ReportsLayout>
  );
};

const StatTile = ({ label, value, hint, tone = 'default' }) => (
  <Stat label={label} value={value} hint={hint} delta={tone === 'rose' ? { text: 'Watch', tone: 'stop' } : undefined} />
);

export default ServiceModeTimelinePage;
